/**
 * לוגיקת תצוגה לחבילות נופש: dedupe חוקים, סיווג מתנות/כלולות/שדרוג בתשלום,
 * וסכום חייב ללקוח — מעדיף meta מהשרת, לא סכימה ישירה של lines[].total_gross.
 */

/** @param {Record<string, unknown>|null|undefined} r */
export function getRuleDisplayMode(r) {
  if (!r || typeof r !== "object") return "";
  const v = r.display_mode ?? r.displayMode;
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

/** @param {Record<string, unknown>|null|undefined} r */
export function getRulePricingMode(r) {
  if (!r || typeof r !== "object") return "";
  const v = r.pricing_mode ?? r.pricingMode;
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

/**
 * לדיבוג: איפה נפל חוק ב-partition (כלול / מתנה / אופציונלי).
 * @param {Record<string, unknown>} r
 */
export function classifyAttractionRuleBucket(r) {
  if (!r || typeof r !== "object") return "optional";
  if (
    r.included_in_package === true ||
    r.included_in_package === "true" ||
    r.includedInPackage === true
  ) {
    return "included";
  }
  const dm = getRuleDisplayMode(r);
  const pm = getRulePricingMode(r);
  if (dm === "gift") return "gift";
  if (
    pm === "hidden_in_package" ||
    pm === "included" ||
    dm === "included" ||
    dm === "hidden_in_package"
  ) {
    return "included";
  }
  return "optional";
}

/**
 * @param {Array<Record<string, unknown>>} rules
 * @returns {Array<{ attraction_id: unknown, display_mode: string, pricing_mode: string, bucket: string }>}
 */
export function dumpPartitionForRules(rules) {
  if (!Array.isArray(rules)) return [];
  return rules.map((r) => ({
    attraction_id: r?.attraction_id,
    display_mode: getRuleDisplayMode(r),
    pricing_mode: getRulePricingMode(r),
    bucket: classifyAttractionRuleBucket(r),
  }));
}

/**
 * @param {Array<Record<string, unknown>>} rules
 * @returns {Array<Record<string, unknown>>}
 */
export function dedupeRulesByAttractionId(rules) {
  if (!Array.isArray(rules) || !rules.length) return [];
  const sorted = [...rules].sort((a, b) => {
    const pa = Number(a?.priority) || 0;
    const pb = Number(b?.priority) || 0;
    if (pb !== pa) return pb - pa;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });
  const byAid = new Map();
  for (const r of sorted) {
    const aid = r?.attraction_id;
    if (aid == null || Number.isNaN(Number(aid))) continue;
    const n = Number(aid);
    if (!byAid.has(n)) byAid.set(n, r);
  }
  return Array.from(byAid.values());
}

/**
 * Dedupe לפרופיל חבילה: כשיש כמה חוקים לאותו attraction_id, מעדיף תצוגת
 * מתנה > כלול/מוסתר בחבילה > אופציונלי בתשלום — כדי שלא ייעלמו included בגלל priority גבוה יותר לשדרוג.
 * @param {Array<Record<string, unknown>>} rules
 * @returns {Array<Record<string, unknown>>}
 */
/**
 * תווית באדג' על כרטיס מלון בחיפוש — מיושר עם בלוק "אטרקציות בחבילה" (חוקים מ־GET /api/attractions/rules).
 * סדר עדיפות: כלול פעיל → מתנה → אחר (אופציונלי/מומחז); בתוך כל קבוצה: priority ↓, id ↓.
 * @param {Array<Record<string, unknown>>} rules
 * @returns {string}
 */
export function pickPrimaryRuleLabelForHotelSearchBadge(rules) {
  if (!Array.isArray(rules) || rules.length === 0) return "";
  const deduped = dedupeRulesForPackageProfile(rules);
  const rankBucket = (r) => {
    const b = classifyAttractionRuleBucket(r);
    if (b === "included") return 3;
    if (b === "gift") return 2;
    return 1;
  };
  const sorted = [...deduped].sort((a, b) => {
    const ra = rankBucket(a);
    const rb = rankBucket(b);
    if (rb !== ra) return rb - ra;
    const pa = Number(a?.priority) || 0;
    const pb = Number(b?.priority) || 0;
    if (pb !== pa) return pb - pa;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });
  const best = sorted[0];
  if (!best || typeof best !== "object") return "";
  return String(
    best.display_text ||
      best.displayText ||
      best.name ||
      best.badge_text ||
      best.badgeText ||
      ""
  ).trim();
}

export function dedupeRulesForPackageProfile(rules) {
  if (!Array.isArray(rules) || !rules.length) return [];
  const byAid = new Map();
  /** עקביות מלאה עם classifyAttractionRuleBucket + partition */
  const rank = (r) => {
    const b = classifyAttractionRuleBucket(r);
    if (b === "gift") return 3;
    if (b === "included") return 2;
    return 1;
  };
  const sortKey = (r) => [
    rank(r),
    Number(r?.priority) || 0,
    Number(r?.id) || 0,
  ];
  const better = (a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    for (let i = 0; i < ka.length; i += 1) {
      if (ka[i] !== kb[i]) return ka[i] > kb[i] ? a : b;
    }
    return a;
  };
  for (const r of rules) {
    const aid = r?.attraction_id;
    if (aid == null || Number.isNaN(Number(aid))) continue;
    const n = Number(aid);
    const prev = byAid.get(n);
    if (!prev) {
      byAid.set(n, r);
    } else {
      byAid.set(n, better(prev, r));
    }
  }
  return Array.from(byAid.values());
}

/**
 * @param {number|string|null|undefined} attractionId
 * @param {Map<number, Record<string, unknown>>|null|undefined} ruleByAttractionId
 */
export function attractionShowsInBillableUpsell(attractionId, ruleByAttractionId) {
  if (attractionId == null || Number.isNaN(Number(attractionId))) return false;
  const map = ruleByAttractionId;
  if (!map || !(map instanceof Map) || map.size === 0) return true;
  const r = map.get(Number(attractionId));
  if (!r) return true;
  const dm = getRuleDisplayMode(r);
  if (dm === "gift") return false;
  const pm = getRulePricingMode(r);
  if (pm === "hidden_in_package") return false;
  if (pm === "included") return false;
  if (dm === "included") return false;
  return true;
}

/**
 * חלוקת חוקים (אחרי dedupe) לתצוגת פרופיל חבילה.
 * @param {Array<Record<string, unknown>>} dedupedRules
 */
export function partitionRulesForPackageProfile(dedupedRules) {
  const included = [];
  const gifts = [];
  for (const r of dedupedRules || []) {
    const b = classifyAttractionRuleBucket(r);
    if (b === "gift") {
      gifts.push(r);
    } else if (b === "included") {
      included.push(r);
    }
  }
  return { included, gifts };
}

/**
 * חוקי שדרוג בתשלום (לא מתנה, לא כלול מוסתר בחבילה).
 * @param {Array<Record<string, unknown>>} dedupedRules
 * @returns {Array<Record<string, unknown>>}
 */
export function partitionOptionalUpsellRules(dedupedRules) {
  const out = [];
  for (const r of dedupedRules || []) {
    if (classifyAttractionRuleBucket(r) === "optional") {
      out.push(r);
    }
  }
  return out;
}

/**
 * @param {Map<number, Record<string, unknown>>} ruleByAttractionId
 */
export function buildRuleByAttractionMap(dedupedRules) {
  const m = new Map();
  for (const r of dedupedRules || []) {
    const aid = r?.attraction_id;
    if (aid == null) continue;
    m.set(Number(aid), r);
  }
  return m;
}

/** @param {Record<string, unknown>|null|undefined} row */
export function dynamicPackageAttractionLineBillableToCustomer(row) {
  const tg = Number(row?.total_gross);
  if (tg > 0) return true;
  const rs = row?.rule_snapshot;
  if (!rs || typeof rs !== "object") return false;
  if (rs.dynamic_package_marketing_only) return false;
  if (String(rs.display_mode || "").toLowerCase() === "gift") return false;
  if (
    rs.dynamic_package_price_unchanged &&
    String(rs.pricing_mode || "").toLowerCase() === "hidden_in_package"
  ) {
    return false;
  }
  return false;
}

/**
 * מקור אמת לסכום תוספות חייב: קודם meta מה-API, אחרת סינון שורות (לא סכימה ישירה של total_gross).
 * @param {{ lines?: unknown[], meta?: { billable_attractions_total_gross?: number } }|null|undefined} payload
 */
export function getBillableAttractionsTotalGross(payload) {
  const meta = payload?.meta?.billable_attractions_total_gross;
  if (meta != null && !Number.isNaN(Number(meta))) {
    return Number(meta);
  }
  const lines = payload?.lines;
  if (!Array.isArray(lines)) return 0;
  return lines.filter(dynamicPackageAttractionLineBillableToCustomer).reduce((s, row) => {
    return s + (Number(row?.total_gross) || 0);
  }, 0);
}
